import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import type { BookStatus } from "@passa-a-pagina/shared";
import { api, ApiError } from "../../lib/api";

interface Stats {
  totals: {
    books: number;
    available: number;
    onHold: number;
    checkedOut: number;
    users: number;
    checkouts: number;
  };
  topBooks: Array<{
    id: string;
    title: string;
    author: string | null;
    coverUrl: string | null;
    checkoutCount: number;
  }>;
  topAuthors: Array<{ author: string; checkoutCount: number }>;
  recentCheckIns: Array<{
    id: string;
    title: string;
    author: string | null;
    coverUrl: string | null;
    scannedAt: string;
    donorEmail: string | null;
  }>;
  recentCheckOuts: Array<{
    holdId: string;
    bookId: string;
    title: string;
    author: string | null;
    coverUrl: string | null;
    userEmail: string;
    releasedAt: string;
  }>;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

interface DrillBookRow {
  id: string;
  isbn: string | null;
  title: string;
  author: string | null;
  coverUrl: string | null;
  status: BookStatus;
  scannedAt: string;
  donorEmail: string | null;
}

interface DrillResponse {
  items: DrillBookRow[];
  total: number;
  pages: number;
}

interface DrillUserRow {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: "user" | "admin";
  points: number;
  createdAt: string;
  activeHolds: number;
  donations: number;
}

interface DrillUsersResponse {
  items: DrillUserRow[];
  total: number;
  pages: number;
}

type DrillFilter = "all" | BookStatus | "users";

export function AdminStats() {
  const { t } = useTranslation();
  const [drill, setDrill] = useState<DrillFilter | null>(null);

  const query = useQuery<Stats, ApiError>({
    queryKey: ["admin", "stats"],
    queryFn: () => api<Stats>("/api/admin/stats"),
  });

  const drillQuery = useQuery<DrillResponse, ApiError>({
    queryKey: ["admin", "stats-drill", drill],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (drill && drill !== "all" && drill !== "users") sp.set("status", drill);
      sp.set("limit", "50");
      return api<DrillResponse>(`/api/admin/books?${sp.toString()}`);
    },
    enabled: drill !== null && drill !== "users",
    retry: false,
  });

  const usersDrillQuery = useQuery<DrillUsersResponse, ApiError>({
    queryKey: ["admin", "stats-drill-users"],
    queryFn: () => api<DrillUsersResponse>("/api/admin/users?limit=100"),
    enabled: drill === "users",
    retry: false,
  });

  if (query.isPending) {
    return <p>{t("common.loading")}</p>;
  }
  if (query.isError || !query.data) {
    return <p className="field__error" role="alert">{query.error?.message ?? t("common.error")}</p>;
  }

  const s = query.data;

  return (
    <section aria-labelledby="stats-title" className="admin__panel admin__stats">
      <h2 id="stats-title">{t("admin.statsTitle")}</h2>

      <ul className="stats-grid" aria-label={t("admin.statsTotals")}>
        {(
          [
            { key: "all" as const, count: s.totals.books, label: t("admin.statsBooksTotal") },
            { key: "available" as const, count: s.totals.available, label: t("admin.statsAvailable") },
            { key: "on_hold" as const, count: s.totals.onHold, label: t("admin.statsOnHold") },
            { key: "checked_out" as const, count: s.totals.checkedOut, label: t("admin.statsCheckedOut") },
          ]
        ).map((card) => {
          const active = drill === card.key;
          return (
            <li key={card.key} className="stats-grid__cell">
              <button
                type="button"
                className={`stats-grid__link ${active ? "stats-grid__link--active" : ""}`}
                aria-pressed={active}
                onClick={() => setDrill(active ? null : card.key)}
              >
                <span className="stats-grid__num">{card.count}</span>
                <span className="stats-grid__label">{card.label}</span>
              </button>
            </li>
          );
        })}
        <li className="stats-grid__cell">
          <button
            type="button"
            className={`stats-grid__link ${drill === "users" ? "stats-grid__link--active" : ""}`}
            aria-pressed={drill === "users"}
            onClick={() => setDrill(drill === "users" ? null : "users")}
          >
            <span className="stats-grid__num">{s.totals.users}</span>
            <span className="stats-grid__label">{t("admin.statsUsers")}</span>
          </button>
        </li>
        <li className="stats-grid__cell">
          <span className="stats-grid__num">{s.totals.checkouts}</span>
          <span className="stats-grid__label">{t("admin.statsCheckouts")}</span>
        </li>
      </ul>

      {drill && (
        <section className="stats-drill" aria-label={t(`admin.stats.drill.${drill}`)}>
          <header className="stats-drill__head">
            <h3>{t(`admin.stats.drill.${drill}`)}</h3>
            <button
              type="button"
              className="stats-drill__close"
              onClick={() => setDrill(null)}
              aria-label={t("admin.stats.close")}
            >
              ✕
            </button>
          </header>

          {drill === "users" ? (
            <>
              {usersDrillQuery.isLoading && <p>{t("common.loading")}</p>}
              {usersDrillQuery.error && (
                <p className="field__error" role="alert">{usersDrillQuery.error.message}</p>
              )}
              {usersDrillQuery.data && (
                <>
                  {usersDrillQuery.data.items.length === 0 ? (
                    <p>{t("admin.stats.noUsers")}</p>
                  ) : (
                    <>
                      <p className="stats-drill__count">
                        {usersDrillQuery.data.total}{" "}
                        {t("admin.stats.userWord", { count: usersDrillQuery.data.total })}
                        {usersDrillQuery.data.total > usersDrillQuery.data.items.length && (
                          <> {t("admin.stats.showingFirst", { count: usersDrillQuery.data.items.length })}</>
                        )}
                      </p>
                      <div className="stats-drill__table-wrap">
                        <table className="stats-drill__users">
                          <thead>
                            <tr>
                              <th scope="col">{t("admin.stats.colEmail")}</th>
                              <th scope="col">{t("admin.stats.colName")}</th>
                              <th scope="col">{t("admin.stats.colSurname")}</th>
                              <th scope="col">{t("admin.stats.colType")}</th>
                              <th scope="col">{t("admin.stats.colPoints")}</th>
                              <th scope="col">{t("admin.stats.colHolds")}</th>
                              <th scope="col">{t("admin.stats.colDonations")}</th>
                              <th scope="col">{t("admin.stats.colRegistered")}</th>
                              <th scope="col"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {usersDrillQuery.data.items.map((u) => (
                              <UserRow key={u.id} user={u} />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {drillQuery.isLoading && <p>{t("common.loading")}</p>}
              {drillQuery.error && (
                <p className="field__error" role="alert">{drillQuery.error.message}</p>
              )}
              {drillQuery.data && (
                <>
                  {drillQuery.data.items.length === 0 ? (
                    <p>{t("admin.stats.noBooks")}</p>
                  ) : (
                    <>
                      <p className="stats-drill__count">
                        {drillQuery.data.total}{" "}
                        {t("admin.stats.bookWord", { count: drillQuery.data.total })}
                        {drillQuery.data.total > drillQuery.data.items.length && (
                          <> {t("admin.stats.showingFirst", { count: drillQuery.data.items.length })}</>
                        )}
                      </p>
                      <ul className="stats-drill__list">
                        {drillQuery.data.items.map((b) => (
                          <li key={b.id} className="stats-drill__item">
                            {b.coverUrl ? (
                              <img src={`/api/cover/${b.id}`} alt="" className="stats-drill__cover" loading="lazy" />
                            ) : (
                              <div className="stats-drill__cover stats-drill__cover--empty" aria-hidden="true">📖</div>
                            )}
                            <div className="stats-drill__meta">
                              <strong>{b.title}</strong>
                              {b.author && <span>{b.author}</span>}
                              <small>
                                {new Date(b.scannedAt).toLocaleDateString("pt-PT")}
                                {b.donorEmail && <> · {b.donorEmail}</>}
                              </small>
                            </div>
                          </li>
                        ))}
                      </ul>
                      {drillQuery.data.total > drillQuery.data.items.length && (
                        <p className="stats-drill__more">
                          <Link to={`/admin/livros${drill !== "all" ? `?status=${drill}` : ""}`}>
                            {t("admin.stats.viewFullList")}
                          </Link>
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </section>
      )}

      <div className="admin__lists">
        <section aria-labelledby="recent-in-title">
          <h3 id="recent-in-title">{t("admin.recentCheckIns")}</h3>
          {s.recentCheckIns.length === 0 ? (
            <p>{t("admin.noData")}</p>
          ) : (
            <ul className="recent-list">
              {s.recentCheckIns.map((b) => (
                <li key={b.id}>
                  <span className="recent-list__title">{b.title}</span>
                  {b.author && <span className="recent-list__sub">{b.author}</span>}
                  <span className="recent-list__meta">
                    {fmtDate(b.scannedAt)}
                    {b.donorEmail && <> · {b.donorEmail}</>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section aria-labelledby="recent-out-title">
          <h3 id="recent-out-title">{t("admin.recentCheckOuts")}</h3>
          {s.recentCheckOuts.length === 0 ? (
            <p>{t("admin.noData")}</p>
          ) : (
            <ul className="recent-list">
              {s.recentCheckOuts.map((h) => (
                <li key={h.holdId}>
                  <span className="recent-list__title">{h.title}</span>
                  {h.author && <span className="recent-list__sub">{h.author}</span>}
                  <span className="recent-list__meta">
                    {fmtDate(h.releasedAt)} · {h.userEmail}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="admin__lists">
        <section aria-labelledby="top-books-title">
          <h3 id="top-books-title">{t("admin.topBooks")}</h3>
          {s.topBooks.length === 0 ? (
            <p>{t("admin.noData")}</p>
          ) : (
            <ol className="rank-list">
              {s.topBooks.map((b) => (
                <li key={b.id}>
                  <span className="rank-list__title">{b.title}</span>
                  {b.author && <span className="rank-list__sub">{b.author}</span>}
                  <span className="rank-list__count">{b.checkoutCount}</span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section aria-labelledby="top-authors-title">
          <h3 id="top-authors-title">{t("admin.topAuthors")}</h3>
          {s.topAuthors.length === 0 ? (
            <p>{t("admin.noData")}</p>
          ) : (
            <ol className="rank-list">
              {s.topAuthors.map((a) => (
                <li key={a.author}>
                  <span className="rank-list__title">{a.author}</span>
                  <span className="rank-list__count">{a.checkoutCount}</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </section>
  );
}

/**
 * One row in the stats users drill table. firstName / lastName are inline
 * editable inputs that PATCH on blur — admin-only feature, so no extra
 * permission gating is needed beyond the route's requireAdmin.
 */
function UserRow({ user }: { user: DrillUserRow }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [firstName, setFirstName] = useState(user.firstName ?? "");
  const [lastName, setLastName] = useState(user.lastName ?? "");
  const [email, setEmail] = useState(user.email);
  const [points, setPoints] = useState(String(user.points));
  const [saving, setSaving] = useState<null | "first" | "last" | "email" | "points">(null);
  const [err, setErr] = useState<string | null>(null);

  const patch = useMutation({
    mutationFn: (body: {
      firstName?: string | null;
      lastName?: string | null;
      email?: string;
      points?: number;
    }) =>
      api<{
        id: string;
        email: string;
        firstName: string | null;
        lastName: string | null;
        points: number;
      }>(`/api/admin/users/${user.id}`, { method: "PATCH", body }),
    onMutate: (body) => {
      setErr(null);
      setSaving(
        body.firstName !== undefined
          ? "first"
          : body.lastName !== undefined
            ? "last"
            : body.email !== undefined
              ? "email"
              : "points",
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "stats-drill-users"] });
    },
    onError: (e: ApiError) => setErr(e.message),
    onSettled: () => setSaving(null),
  });

  function commitFirst() {
    const v = firstName.trim();
    if ((user.firstName ?? "") === v) return;
    patch.mutate({ firstName: v });
  }
  function commitLast() {
    const v = lastName.trim();
    if ((user.lastName ?? "") === v) return;
    patch.mutate({ lastName: v });
  }
  function commitEmail() {
    const v = email.trim();
    if (v.toLowerCase() === user.email.toLowerCase()) return;
    if (!/^\S+@\S+\.\S+$/.test(v)) {
      setErr(t("admin.stats.emailInvalid"));
      setEmail(user.email); // revert
      return;
    }
    patch.mutate({ email: v });
  }
  function commitPoints() {
    const n = parseInt(points, 10);
    if (Number.isNaN(n) || n < 0) {
      setErr(t("admin.stats.pointsInvalid"));
      setPoints(String(user.points)); // revert
      return;
    }
    if (n === user.points) return;
    patch.mutate({ points: n });
  }

  return (
    <tr>
      <td>
        <input
          type="text"
          inputMode="email"
          className="stats-drill__name-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={commitEmail}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          aria-label={t("admin.stats.emailAria")}
          disabled={saving !== null}
        />
        {saving === "email" && <span className="stats-drill__saving">…</span>}
      </td>
      <td>
        <input
          type="text"
          className="stats-drill__name-input"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          onBlur={commitFirst}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          placeholder="—"
          aria-label={t("admin.stats.firstNameAria")}
          disabled={saving !== null}
        />
        {saving === "first" && <span className="stats-drill__saving">…</span>}
      </td>
      <td>
        <input
          type="text"
          className="stats-drill__name-input"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          onBlur={commitLast}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          placeholder="—"
          aria-label={t("admin.stats.lastNameAria")}
          disabled={saving !== null}
        />
        {saving === "last" && <span className="stats-drill__saving">…</span>}
      </td>
      <td>{user.role === "admin" ? t("admin.stats.roleAdmin") : t("admin.stats.roleUser")}</td>
      <td className="stats-drill__num">
        <input
          type="number"
          min={0}
          className="stats-drill__points-input"
          value={points}
          onChange={(e) => setPoints(e.target.value)}
          onBlur={commitPoints}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          aria-label={t("admin.stats.pointsAria")}
          disabled={saving !== null}
        />
        {saving === "points" && <span className="stats-drill__saving">…</span>}
      </td>
      <td className="stats-drill__num">{user.activeHolds}</td>
      <td className="stats-drill__num">{user.donations}</td>
      <td>{new Date(user.createdAt).toLocaleDateString("pt-PT")}</td>
      <td>
        {err && (
          <p className="field__error stats-drill__row-error" role="alert">
            {err}
          </p>
        )}
        <Link
          to={`/admin/creditos?email=${encodeURIComponent(user.email)}`}
          className="stats-drill__action"
        >
          Créditos
        </Link>
      </td>
    </tr>
  );
}
